import React, { useEffect, useState, useRef } from 'react';

// API/Components
import {
	ANALYSIS_RESULT,
	GRAPH_BOUNDS,
	validValue,
	AnalysisResultToStateColor,
	measUnitsConversion,
	getUserTempUnits,
	getMeasurementAnalysis,
} from '../enums';
import { DrawVerticalDashedLineAtTooltip } from '../chart_helpers';
import ResetZoomButton from '../reset_zoom_button';
import CustomGraphLegend from '../custom_graph_legend';

// Material UI
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';

// Chart.JS
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	Tooltip as ChartJSTooltip,
	BarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	ChartJSTooltip,
	BarController,
	zoomPlugin,
);

// Helper function to sort temperature datasets in proper order
function sortTemperatureItems(a, b, getLabel) {
	// Get labels using the provided getter function
	const aLabel = getLabel(a);
	const bLabel = getLabel(b);
	
	// Define sort order: Temperature, Ambient Temperature, Critical High, Critical Low
	const itemOrder = {
		'Temperature': 0,  // Matches labels that start with "Temperature ("
		'Ambient Temperature': 1,
		'Critical High': 2,
		'Critical Low': 3,
	};
	
	// Get order values, checking for Temperature label variations
	let aOrder = 999;
	let bOrder = 999;
	
	if (aLabel) {
		if (aLabel.startsWith('Temperature')) {
			aOrder = itemOrder['Temperature'];  // Handles "Temperature (°C)" or "Temperature (°F)"
		} else {
			aOrder = itemOrder[aLabel] ?? 999;
		}
	}
	
	if (bLabel) {
		if (bLabel.startsWith('Temperature')) {
			bOrder = itemOrder['Temperature'];  // Handles "Temperature (°C)" or "Temperature (°F)"
		} else {
			bOrder = itemOrder[bLabel] ?? 999;
		}
	}
	
	return aOrder - bOrder;
}

// Temperature Realtime Graph Component
export default function TemperatureRealtimeGraph(props) {

	const { realtimeData, sysLimitData, sysGraphScales, graphScales, selectedString, loading } = props;

	const [graphData, setGraphData] = useState({});
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		ambientTempEnabled: true,
		unitTempsView: false,
	});
	const [graphOptions, setGraphOptions] = useState({});
	const [tempSensorsMapped, setTempSensorsMapped] = useState(false);

	const chartRef = useRef(null);
	const hasAutoInitializedRef = useRef(false);
	const initializedRef = useRef(false);

	// Get the active limit data based on current data source configuration
	const getActiveRealtimeData = () => {
		return graphSettings.unitTempsView ? realtimeData?.units : realtimeData?.temperatures;
	};

	// Get the active graph scales based on current data source configuration
	const getActiveGraphScales = () => {
		return graphSettings.unitTempsView ? graphScales : sysGraphScales;
	};

	// Initialize chart options once on first load
	useEffect(() => {
		if (initializedRef.current) { return; }
		
		const activeScales = getActiveGraphScales();
		if (!activeScales) { return; }

		const { tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax } = getAxisRanges(activeScales);
		const options = createChartOptions(tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax);
		setGraphOptions(options);
		
		initializedRef.current = true;
	}, [graphScales, sysGraphScales, graphSettings.unitTempsView]);

	// Process temperature realtime data and update graph datasets
	useEffect(() => {
		const activeRealtimeData = getActiveRealtimeData();
		
		if (!activeRealtimeData || activeRealtimeData.length === 0 || !sysLimitData || sysLimitData.length === 0) { 
			return;
		}

		// Check if temperature sensors are mapped to units
		const areTempsensorsMapped = Boolean(realtimeData?.system?.temp_sensors_mapped);
		setTempSensorsMapped(areTempsensorsMapped);
		
		// Auto-enable unit temps view on first load if sensors are mapped, but force sensor view if not
		if (areTempsensorsMapped && !hasAutoInitializedRef.current) {
			setGraphSettings(prevSettings => ({ ...prevSettings, unitTempsView: true }));
			hasAutoInitializedRef.current = true;
		} else if (!areTempsensorsMapped && graphSettings.unitTempsView) {
			setGraphSettings(prevSettings => ({ ...prevSettings, unitTempsView: false }));
		}

		// Filter data based on selectedString when temp sensors are mapped
		let filteredData = activeRealtimeData;
		if (selectedString !== 0 && selectedString !== null && selectedString !== undefined && areTempsensorsMapped) {
			if (graphSettings.unitTempsView) {
				// Units view: filter units by string_id
				filteredData = activeRealtimeData.filter(unit => unit.string_id === selectedString);
			} else {
				// Temp sensors view: filter sensors by temp_sensor_id from units in selected string
				const unitsInString = realtimeData?.units?.filter(unit => unit.string_id === selectedString) || [];
				const sensorIds = [...new Set(unitsInString.map(unit => unit.temp_sensor_id).filter(id => id !== null && id !== undefined))];
				filteredData = activeRealtimeData.filter(sensor => sensorIds.includes(sensor.sensor_id));
			}
			
			// If no data matches the filter, clear the chart
			if (filteredData.length === 0) {
				setGraphData({ labels: [], datasets: [] });
				return;
			}
		}

		// Process temperature data with active configuration
		const processedData = processTemperatureData(filteredData, sysLimitData, realtimeData?.system?.ambient_temp);
		
		// Build chart datasets
		let datasets = buildTemperatureDatasets(processedData, sysLimitData);

		const dataLabels = graphSettings.unitTempsView ?
			filteredData.map(s => `S${s.string_id} U${s.unit_id}`) :
			filteredData.map(s => `Sensor ${s.sensor_id}`);

		// If chart exists, update directly (preserves zoom/pan)
		if (chartRef.current && chartRef.current.data) {
			chartRef.current.data.labels = dataLabels;
			chartRef.current.data.datasets = datasets;
			chartRef.current.update('none'); // Update without animation
		} else {
			// Initial load: create and set graph data state
			const data = {
				labels: dataLabels,
				datasets: datasets,
			};
			setGraphData(data);
		}

	}, [realtimeData, graphSettings, sysLimitData, selectedString]);

	// Process raw temperature data with unit conversion and analysis
	function processTemperatureData(measurements, limitData, ambientTemp) {
		const temperatures = measurements.map(m => {
			const temp = validValue(m.temperature, null);
			return temp !== null ? measUnitsConversion(temp) : null;
		});
		
		const analyses = measurements.map(m => getMeasurementAnalysis(m.temperature, limitData.hi_crit, limitData.lo_crit, null, null));

		const convertedAmbient = (ambientTemp !== null && ambientTemp !== undefined) ? measUnitsConversion(ambientTemp) : null;

		return {
			temperatures,
			analyses,
			ambientTemperature: convertedAmbient,
			sensorCount: measurements.length,
		};
	}

	// Build all chart datasets from processed data
	function buildTemperatureDatasets(processedData, limitData) {
		const { temperatures, analyses, ambientTemperature, sensorCount } = processedData;
		
		// Start with temperature bars
		let datasets = createTemperatureDataset(temperatures, analyses);
		
		// Add ambient line if available
		if (ambientTemperature !== null) {
			datasets = addAmbientLine(datasets, ambientTemperature, graphSettings.ambientTempEnabled, sensorCount);
		}
		
		// Add limit lines if enabled
		datasets = addLimitDatasets(datasets, limitData, graphSettings.limitsEnabled, sensorCount);

		return datasets;
	}

	// Calculate axis ranges for temperature based on data and extremes
	function getAxisRanges(graphScales) {

		// Initial display range uses graphScales if available, otherwise GRAPH_BOUNDS defaults
		const tempDisplayMax = measUnitsConversion(validValue(graphScales?.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max));
		const tempDisplayMin = measUnitsConversion(validValue(graphScales?.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min));

		// Pan/zoom limits always use GRAPH_BOUNDS bounds
		const tempBoundsMax = measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max);
		const tempBoundsMin = measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min);

		return { tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax };
	};

	// Create temperature bar dataset with analysis-based colors
	function createTemperatureDataset(temperatures, analyses) {
		const temperatureDataset = {
			label: `Temperature (${getUserTempUnits()})`,
			data: temperatures,
			backgroundColor: temperatures.map((t, idx) => {
				const analysisColor = AnalysisResultToStateColor(analyses[idx].result);
				return tinycolor(analysisColor).setAlpha(0.7).toRgbString();
			}),
			borderColor: temperatures.map((t, idx) => {
				const analysisColor = AnalysisResultToStateColor(analyses[idx].result);
				return tinycolor(analysisColor).darken(15).toHexString();
			}),
			borderWidth: 2,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 1,
		};

		return [temperatureDataset];
	};

	// Create a limit line dataset
	function createLimitLine(label, value, color, sensorCount) {
		const convertedValue = measUnitsConversion(value);
		return {
			label,
			data: Array(sensorCount).fill(convertedValue),
			type: 'line',
			borderColor: color,
			backgroundColor: tinycolor(color).setAlpha(0.7).toRgbString(),
			borderWidth: 2,
			borderDash: [5, 5],
			pointRadius: 0,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 0,
		};
	}

	// Add limit line datasets if enabled and limit data exists
	function addLimitDatasets(datasets, limitData, limitsEnabled, sensorCount) {
		if (!limitsEnabled || !limitData) {
			return datasets;
		}

		// Get critical limit colors
		const highCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		const lowCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		// Add high critical limit if available
		if (limitData.hi_crit !== undefined && limitData.hi_crit !== null) {
			datasets.push(createLimitLine('Crit High', limitData.hi_crit, highCritColor, sensorCount));
		}

		// Add low critical limit if available
		if (limitData.lo_crit !== undefined && limitData.lo_crit !== null) {
			datasets.push(createLimitLine('Crit Low', limitData.lo_crit, lowCritColor, sensorCount));
		}

		return datasets;
	};

	// Add ambient temperature line
	function addAmbientLine(datasets, ambientTemp, ambientEnabled, sensorCount) {
		if (!ambientEnabled) {
			return datasets;
		}
		
		datasets.push({
			label: 'Ambient Temperature',
			data: Array(sensorCount).fill(ambientTemp),
			type: 'line',
			borderColor: 'rgb(35, 141, 193)', // Blue color for ambient
			backgroundColor: 'rgba(35, 141, 193, 0.7)',
			borderWidth: 3,
			pointRadius: 0,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 2,
		});

		return datasets;
	};

	// Create chart options configuration
	function createChartOptions(tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax) {
		return {
			maintainAspectRatio: false,
			animation: false,
			responsive: true,
			interaction: {
				mode: 'index',
				intersect: false,
			},
			scales: {
				x: {
					grid: {
						display: false,
					},
				},
				'y': {
					type: 'linear',
					position: 'left',
					min: tempDisplayMin,
					max: tempDisplayMax,
					title: {
						display: true,
						text: `Temperature (${getUserTempUnits()})`,
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						color: 'rgba(0, 0, 0, 0.1)',
					},
				},
			},
			plugins: {
				legend: {
					display: false,
				},
				tooltip: {
					filter: function(tooltipItem) {
						// Only show tooltip items that have actual values (not null)
						return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
					},
					itemSort: (a, b) => sortTemperatureItems(a, b, (item) => item.dataset.label),
					callbacks: {
						beforeLabel: function(context) {
							// Check if this is the first limit item (after temperature)
							const currentIsLimit = !context.dataset.label?.includes('Temperature');
							
							if (currentIsLimit && context.datasetIndex > 0) {
								// Find the actual previous item in the sorted tooltip
								const tooltipItems = context.chart.tooltip.dataPoints;
								const currentIndex = tooltipItems.findIndex(item => 
									item.datasetIndex === context.datasetIndex
								);
								
								if (currentIndex > 0) {
									const prevItem = tooltipItems[currentIndex - 1];
									const prevItemIsTemperature = prevItem.dataset.label?.includes('Temperature');
									
									if (prevItemIsTemperature) {
										return '\n─── Limits ───';
									}
								}
							}
							return null;
						},
						label: function(context) {
							const datasetLabel = context.dataset.label || '';
							const displayValue = context.parsed.y;

							// Format temperature with appropriate unit
							return `${datasetLabel}: ${displayValue.toFixed(1)} ${getUserTempUnits()}`;
						},
					},
				},
				zoom: {
					pan: {
						enabled: true,
						mode: 'xy',
					},
					limits: {
						'y': {
							min: tempBoundsMin,
							max: tempBoundsMax,
							minRange: 5,
						},
					},
					zoom: {
						mode: 'y',
						wheel: {
							enabled: true,
							speed: 0.05,
						},
						pinch: {
							enabled: true,
						},
						onZoomStart: (e) => {
							if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
								return;
							}
							if (e.event.shiftKey === true) {
								e.chart.config.options.plugins.zoom.zoom.mode = "x";
								e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
							} else {
								e.chart.config.options.plugins.zoom.zoom.mode = "y";
								e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
							}
						},
					},
				},
			},
		};
	};
	
	// Toggle limit markers on the graph
	const handleLimitMarkersToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			limitsEnabled: !prev.limitsEnabled,
		}));
	};

	// Toggle ambient temperature line
	const handleAmbientTempToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			ambientTempEnabled: !prev.ambientTempEnabled,
		}));
	};

	// Toggle between system and unit data sources
	const handleSensorViewToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			unitTempsView: !prev.unitTempsView
		}));
	};
	
	const handleGraphResetZoom = () => {
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingLeft: '5px',
				paddingRight: '5px',
				paddingBottom: '3px',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			<div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center', flexShrink: 0 }}>
				<strong>
					{ graphSettings.unitTempsView ? 'Unit Temperatures' : 'Temperature Sensors' }
					{ (tempSensorsMapped && selectedString !== null && selectedString !== undefined && selectedString > 0) ? ` (String ${selectedString})` : ''}
				</strong>
			</div>
			<div style={{ 
				flex: '1 1 auto',
				width: '100%',
				minHeight: 0,
				overflow: 'hidden',
			}}>
				{ (graphData && graphData.datasets && graphData.datasets.length > 0) ? (
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
						plugins={[{ afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart) }]}
					/>
				) : (
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						color: '#666',
					}}>
						{loading ? 'Loading...' : 'No data available'}
					</div>
				)}
			</div>

			<Grid container spacing={1} alignItems="center">
				<Grid item>
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '2px' }}
					>
						<Tooltip title="Toggle limit markers on the graph" arrow>
							<ToggleButton
								value="limitMarkers"
								size="small"
								selected={graphSettings.limitsEnabled}
								onClick={handleLimitMarkersToggle}
								sx={{
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Limits
							</ToggleButton>
						</Tooltip>
						<Tooltip title="Toggle ambient temperature line" arrow>
							<ToggleButton
								value="ambientTempLine"
								size="small"
								selected={graphSettings.ambientTempEnabled}
								onClick={handleAmbientTempToggle}
								sx={{
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Ambient
							</ToggleButton>
						</Tooltip>
						{ tempSensorsMapped && (
							<Tooltip title="Toggle between temperature sensor and unit views" arrow>
								<ToggleButton
									value="sensorView"
									size="small"
									selected={graphSettings.unitTempsView}
									onClick={handleSensorViewToggle}
									sx={{ 
										padding: '2px 8px',
										fontSize: '12px',
										minWidth: 'auto',
									}}
								>
									Units
								</ToggleButton>
							</Tooltip>
						)}
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 0.5, columnGap: 2, padding: '4px 0' }}>
					<CustomGraphLegend
						datasets={graphData.datasets}
						sortFunction={sortTemperatureItems}
						customColorForArrays={AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS)}
					/>
				</Grid>
				<Grid item>
					<ResetZoomButton onClick={handleGraphResetZoom} />
				</Grid>
			</Grid>
		</div>
	);
}